// Import the appropriate Firebase Timestamp based on the environment
let FirebaseTimestamp: any;

try {
  // Try to import the admin version first (for backend)
  FirebaseTimestamp = require('firebase-admin/firestore').Timestamp;
} catch (e) {
  try {
    // Fall back to client version if admin is not available (for frontend)
    FirebaseTimestamp = require('firebase/firestore').Timestamp;
  } catch (e) {
    // Fallback for cases where neither is available (e.g., during tests)
    console.warn('Firebase Timestamp not available, using fallback');
    class FallbackTimestamp {
      toDate() { return new Date(); }
      static now() { return { toDate: () => new Date() }; }
    }
    FirebaseTimestamp = FallbackTimestamp as any;
  }
}

// Utility to convert a date value to YYYY-MM-DD string in local time
function toLocalDateString(dateVal: any, includeTime?: boolean): string | undefined {
  if (!dateVal) return '';
  let d: Date;
  if (dateVal instanceof FirebaseTimestamp) {
    d = dateVal.toDate();
    console.log('[toLocalDateString] d', d)
  } else if (dateVal?._seconds !== undefined) {
    // Handle case where _nanoseconds might be missing
    const seconds = dateVal._seconds || 0;
    const nanoseconds = dateVal._nanoseconds || 0;
    d = new Date(seconds * 1000 + nanoseconds / 1000000);
  } else if (dateVal?.__time__?._seconds !== undefined) {
    // Handle case where _nanoseconds might be missing in nested __time__ object
    const seconds = dateVal.__time__._seconds || 0;
    const nanoseconds = dateVal.__time__._nanoseconds || 0;
    d = new Date(seconds * 1000 + nanoseconds / 1000000);
  } else if (typeof dateVal === 'string' && /T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(dateVal)) {
    // ISO Zulu string, parse as UTC and convert to local time
    const utc = new Date(dateVal);
    d = new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate(), utc.getUTCHours(), utc.getUTCMinutes(), utc.getUTCSeconds(), utc.getUTCMilliseconds());
  } else {
    d = new Date(dateVal);
  }
  if (isNaN(d.getTime())) return '';
  // Format as DD/MM/YYYY for AU or fallback to YYYY-MM-DD
  const userLocale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-AU';
  if (userLocale.startsWith('en-AU')) {
    let dateStr = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    if (includeTime) {
      dateStr += ` ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    }
    return dateStr;
  } else if (userLocale === null || userLocale === undefined) {
    let dateStr = `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
    if (includeTime) {
      dateStr += ` ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    }
    return dateStr;
  } else if (userLocale !== null && userLocale !== undefined) {
    // Convert to their locale
    return d.toLocaleDateString(userLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }
}

function cleanDateAndTimeObject(dateObj: any, addTime?: boolean): Date | null {
  try {
    if (!dateObj) return null;
    let d: Date;
    if (dateObj.toDate && typeof dateObj.toDate === 'function') {
      d = dateObj.toDate();
    } else if (dateObj instanceof FirebaseTimestamp) {
      d = dateObj.toDate();
    } else if (dateObj?._seconds !== undefined) {
      // Handle case where _nanoseconds might be missing
      const seconds = dateObj._seconds || 0;
      const nanoseconds = dateObj._nanoseconds || 0;
      d = new Date(seconds * 1000 + nanoseconds / 1000000);
    } else if (dateObj?.__time__?._seconds !== undefined) {
      // Handle case where _nanoseconds might be missing in nested __time__ object
      const seconds = dateObj.__time__._seconds || 0;
      const nanoseconds = dateObj.__time__._nanoseconds || 0;
      d = new Date(seconds * 1000 + nanoseconds / 1000000);
    } else if (typeof dateObj === 'string' && /T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(dateObj)) {
      // ISO Zulu string, parse as UTC and convert to local time
      const utc = new Date(dateObj);
      d = new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate(), utc.getUTCHours(), utc.getUTCMinutes(), utc.getUTCSeconds(), utc.getUTCMilliseconds());
    } else {
      d = new Date(dateObj);
    }
    if (isNaN(d.getTime())) {
      if (addTime) {
        d.setHours(new Date().getHours());
        d.setMinutes(new Date().getMinutes());
        d.setSeconds(new Date().getSeconds());
        d.setMilliseconds(new Date().getMilliseconds());
      } else {
        console.warn('Failed to parse date (found no time):', dateObj);
        return null;
      }

    }
    return d;
  } catch (error) {
    console.error('Failed to parse date:', error);
    return null;
  }
}

// Utility function to safely render date fields from various sources
// Supports multiple date formats:
// - Firebase Timestamp objects with toDate() method
// - Objects with __time__ property
// - Raw Firestore timestamp objects with seconds/nanoseconds
// - Standard JavaScript Date objects
// - String dates in various formats
// - Numeric timestamps (both millisecond and second-based)
function renderDate(
  dateObj: any,
  format?: Intl.DateTimeFormatOptions
): string {
  if (!dateObj) return '';

  const userLocale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-AU'; // Use browser's locale
  const defaultFormat: Intl.DateTimeFormatOptions = format || {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };

  // Helper function to format date using locale
  const formatDate = (date: Date): string => {
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(userLocale, defaultFormat);
    }
    return 'Invalid date';
  };

  // Convert to Date object based on type
  const toDateObject = (): Date | null => {

    if (dateObj instanceof FirebaseTimestamp) {
      return dateObj.toDate();
    }
    // Handle different date formats
    if (typeof dateObj === 'object') {
      // Handle Firebase Timestamp objects (with toDate function)
      if (dateObj.toDate && typeof dateObj.toDate === 'function') {
        return dateObj.toDate();
      }

      // Handle Firestore Timestamp objects (with or without _nanoseconds)
      if (dateObj._seconds !== undefined) {
        const seconds = dateObj._seconds || 0;
        const nanoseconds = dateObj._nanoseconds || 0;
        return new Date(seconds * 1000 + nanoseconds / 1000000);
      }

      // Handle objects with __time__ property
      if (dateObj.__time__ && typeof dateObj.__time__ === 'object') {
        return new Date(dateObj.__time__);
      }

      // Handle Date objects
      if (dateObj instanceof Date) {
        return dateObj;
      }

      // Try to extract seconds and nanoseconds from Firestore timestamp-like objects
      if (dateObj._seconds && typeof dateObj._seconds === 'number') {
        const seconds = dateObj._seconds;
        const nanoseconds = dateObj._nanoseconds || 0;
        return new Date(seconds * 1000 + nanoseconds / 1000000);
      }

      // Try as-is as last resort for objects
      try {
        const date = new Date(dateObj);
        if (!isNaN(date.getTime())) return date;
      } catch (e) {
        console.warn('Failed to parse object as date:', dateObj);
      }

      return null;
    }

    // Handle string dates
    if (typeof dateObj === 'string') {
      if (!dateObj.trim()) return null;
      try {
        const date = new Date(dateObj);
        if (!isNaN(date.getTime())) return date;
        return null;
      } catch (e) {
        console.warn('Failed to parse string as date:', dateObj);
        return null;
      }
    }

    // Handle number (epoch/unix timestamp)
    if (typeof dateObj === 'number') {
      // Check if milliseconds or seconds (if seconds, convert to ms)
      return new Date(dateObj > 10000000000 ? dateObj : dateObj * 1000);
    }

    return null;
  };

  // Try to convert to date and format
  const dateObject = toDateObject();
  if (dateObject) {
    return formatDate(dateObject);
  }

  // Fallback: return as string if we couldn't parse it
  if (typeof dateObj === 'string') {
    return dateObj; // Return the original string if it's not a valid date
  }

  return String(dateObj);
}


export default {
  toLocalDateString,
  cleanDateAndTimeObject,
  renderDate
}
export {
  toLocalDateString,
  cleanDateAndTimeObject,
  renderDate
}